/** Map forgot-password API errors to user-friendly messages. */

const GENERIC_SERVER_MESSAGES = [
  'something went very wrong',
  'something went wrong',
  'internal server error',
  'server error',
];

function isGenericServerMessage(message) {
  const normalized = String(message || '').trim().toLowerCase();
  return GENERIC_SERVER_MESSAGES.some((phrase) => normalized.includes(phrase));
}

export function readApiErrorMessage(err) {
  if (!err) return '';
  if (typeof err === 'string') return err;
  const data = err.response?.data;
  if (typeof data === 'string') return data;
  if (data && typeof data === 'object' && data.message) return String(data.message);
  return String(err.message || '');
}

export function classifyForgotPasswordError(err) {
  const status = Number(err?.status || err?.response?.status || 0);
  const message = readApiErrorMessage(err).toLowerCase();

  if (status === 404 || message.includes('user not found')) {
    return 'not_found';
  }

  if (
    status === 503 ||
    status >= 500 ||
    message.includes('something went very wrong') ||
    message.includes('unable to send reset email') ||
    message.includes('email is not configured') ||
    message.includes('email template not found') ||
    message.includes('smtp')
  ) {
    return 'email_unavailable';
  }

  return 'unknown';
}

export function getForgotPasswordErrorMessage(err, t) {
  const kind = classifyForgotPasswordError(err);
  const apiMessage = readApiErrorMessage(err);

  if (kind === 'not_found') {
    return t('forgotPassword.errors.notFound', {
      defaultValue: 'No account found for this email address.',
    });
  }

  if (kind === 'email_unavailable') {
    if (apiMessage && !isGenericServerMessage(apiMessage)) {
      return apiMessage;
    }
    return t('forgotPassword.errors.emailUnavailable', {
      defaultValue:
        'We could not send your password reset email right now. Please wait a few minutes and try again. If the problem continues, contact our support team.',
    });
  }

  if (apiMessage && !isGenericServerMessage(apiMessage)) {
    return apiMessage;
  }

  return t('forgotPassword.errors.failed', {
    defaultValue:
      'We could not send your password reset email. Please check your email address and try again.',
  });
}

export function getResetPasswordErrorMessage(err, t) {
  const status = Number(err?.status || err?.response?.status || 0);
  const message = readApiErrorMessage(err).toLowerCase();

  if (status === 404 || message.includes('user not found')) {
    return t('resetPassword.errors.notFound', {
      defaultValue: 'No account found for this email address.',
    });
  }

  if (message.includes('invalid') && message.includes('code')) {
    return t('resetPassword.errors.invalidCode', {
      defaultValue: 'Invalid or expired code. Request a new code and try again.',
    });
  }

  const apiMessage = readApiErrorMessage(err);
  if (apiMessage && !isGenericServerMessage(apiMessage)) {
    return apiMessage;
  }

  return t('resetPassword.errors.failed', {
    defaultValue: 'Failed to reset password. Check the code and try again.',
  });
}
